import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TokenAuthProvider } from './providers/TokenAuthProvider';
import OnboardingFlow from './components/long-onboarding/OnboardingFlow';
import { AmplitudeProvider } from './providers/AmplitudeProvider';
import { AmplitudeExperimentProvider } from './providers/AmplitudeExperimentProvider';
import { GTMProvider } from './providers/GTMProvider';
import { FacebookPixelProvider } from './providers/FacebookPixelProvider';
import { storeFbCookies } from './analytics/metaAnalytics';

// Persist UTM params from URL to localStorage on first load
const utmParams = new URLSearchParams(window.location.search);
['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
  const value = utmParams.get(key);
  if (value) localStorage.setItem(key, value);
});

// Persist Facebook cookie values (fbp/fbc) to localStorage on first load
storeFbCookies();

const Login = React.lazy(() => import('./components/auth/Login'));
const DashboardLayout = React.lazy(() => import('./components/layout/DashboardLayout'));
const AccountPage = React.lazy(() => import('./pages/AccountPage'));
const CancellationPage = React.lazy(() => import('./pages/CancellationPage'));
const FitBandPage = React.lazy(() => import('./pages/FitBandPage'));
const ShortOnboardingFlow = React.lazy(() => import('./components/short-onboarding/OnboardingFlow'));
const DefaultOnboardingFlow = React.lazy(() => import('./components/default-onboarding/OnboardingFlow'));
const DefaultNoScratchFlow = React.lazy(() => import('./components/default-onboarding-no-scratch/OnboardingFlow'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const funnelType = process.env.REACT_APP_FUNNEL_TYPE || 'long';
const funnelMap: Record<string, React.ComponentType> = {
  short: ShortOnboardingFlow,
  long: OnboardingFlow,
  default: DefaultOnboardingFlow,
  'default-no-scratch': DefaultNoScratchFlow,
};
const HomeFunnel = funnelMap[funnelType] || DefaultOnboardingFlow;

const App: React.FC = () => {
  return (
    <AmplitudeProvider apiKey={process.env.REACT_APP_AMPLITUDE_API_KEY || ''}>
      <AmplitudeExperimentProvider>
        <GTMProvider gtmId={process.env.REACT_APP_GTM_ID || ''}>
          <FacebookPixelProvider pixelId={process.env.REACT_APP_PIXEL_ID || ''}>
            <BrowserRouter>
            <AuthProvider>
              <TokenAuthProvider>
                <React.Suspense fallback={null}>
                  <Routes>
                    <Route path="/" element={<HomeFunnel />} />

                    <Route path="/free-band" element={<FitBandPage />} />

                    <Route path="/login" element={<Login />} />

                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <DashboardLayout />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/account"
                      element={
                        <ProtectedRoute>
                          <AccountPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/subscription/cancel"
                      element={
                        <ProtectedRoute>
                          <CancellationPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </React.Suspense>
              </TokenAuthProvider>
            </AuthProvider>
            </BrowserRouter>
          </FacebookPixelProvider >
        </GTMProvider>
      </AmplitudeExperimentProvider>
    </AmplitudeProvider>
  );
};

export default App;
